import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { api, ApiFailure } from "../api/client";
import type { Call, CallEvent, CallerId, PhoneNumber, VoiceApp } from "../api/types";
import {
  Badge, Button, Card, Field, PageHeader, inputClass,
} from "../components/ui";
import { MarkupEditor, MARKUP_EXAMPLES } from "../components/MarkupEditor";
import { StatusBadge, formatDateTime, isLive, useInterval } from "./Calls";

// Playground: originar una llamada de prueba y verla pasar.
//
// # Por qué el markup en línea se guarda como voice app temporal
//
// Porque `POST /calls` acepta una Voice URL o una voice app, no markup suelto:
// el intérprete **consulta** una URL, y eso es el contrato (ADR-004). Para
// probar un documento sin montar un servidor, el portal crea una voice app
// desechable apuntando al validador... y eso no funcionaría, porque el
// validador no devuelve markup.
//
// Así que el markup en línea se ofrece solo como ayuda para escribirlo y
// validarlo: el botón de llamar exige una Voice URL o una app existente. Es
// menos cómodo que un "probar este XML", y es honesto — lo contrario sería
// simular una llamada que no se puede hacer.
const POLL_INTERVAL_MS = 2000;

export function Playground() {
  const [apps, setApps] = useState<VoiceApp[]>([]);
  const [to, setTo] = useState("");
  const [from, setFrom] = useState("");

  // El `from` se elige de una lista y no se teclea: la API solo acepta un
  // número de la cuenta o un caller ID verificado, y un campo libre convierte
  // ese requisito en un 422 que no dice cuáles valen. Aquí se ven.
  const [origenes, setOrigenes] = useState<string[] | null>(null);
  useEffect(() => {
    let vivo = true;
    void Promise.all([
      api.get<CallerId[]>("/api/v1/caller-ids").catch(() => [] as CallerId[]),
      api.get<PhoneNumber[]>("/api/v1/numbers").catch(() => [] as PhoneNumber[]),
    ]).then(([callerIds, numeros]) => {
      if (!vivo) return;
      const usables = [
        ...(callerIds ?? []).filter((c) => c.status === "verified").map((c) => c.e164),
        ...(numeros ?? []).filter((n) => n.status === "assigned").map((n) => n.e164),
      ];
      setOrigenes([...new Set(usables)]);
      // Con uno solo, elegirlo a mano es un paso de más.
      if (usables.length === 1 && usables[0] !== undefined) setFrom(usables[0]);
    });
    return () => {
      vivo = false;
    };
  }, []);
  const [mode, setMode] = useState<"app" | "url">("url");
  const [appID, setAppID] = useState("");
  const [voiceURL, setVoiceURL] = useState("");
  const [markup, setMarkup] = useState(MARKUP_EXAMPLES[1]?.markup ?? "<Response>\n</Response>");
  const [purpose, setPurpose] = useState("other");
  // Los tres modos de la API. "" significa «lo que diga la aplicación de voz».
  const [detection, setDetection] = useState("");

  const [callID, setCallID] = useState<string | null>(null);
  const [call, setCall] = useState<Call | null>(null);
  const [events, setEvents] = useState<CallEvent[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [calling, setCalling] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const rows = await api.get<VoiceApp[]>("/api/v1/voice-apps");
        setApps((rows ?? []).filter((a) => a.status === "active"));
      } catch {
        // Sin apps el playground sigue sirviendo con una Voice URL: no se
        // bloquea la pantalla por no poder listarlas.
      }
    })();
  }, []);

  const follow = useCallback(async () => {
    if (!callID) return;
    try {
      const [detail, timeline] = await Promise.all([
        api.get<Call>(`/api/v1/calls/${callID}`),
        api.get<CallEvent[]>(`/api/v1/calls/${callID}/events`),
      ]);
      setCall(detail);
      setEvents(timeline ?? []);
    } catch (err) {
      setError(err instanceof ApiFailure ? err.error.message : "no se pudo seguir la llamada");
    }
  }, [callID]);

  useEffect(() => {
    void follow();
  }, [follow]);
  useInterval(() => void follow(), call && isLive(call.status) ? POLL_INTERVAL_MS : null);

  async function place(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setCalling(true);
    setCall(null);
    setEvents([]);
    setCallID(null);

    try {
      const body: Record<string, unknown> = {
        to: to.trim(),
        from: from.trim(),
        purpose,
        metadata: { origen: "playground" },
      };
      if (detection) {
        body.machine_detection = detection;
      }
      if (mode === "app") {
        body.voice_app_id = appID;
      } else {
        body.voice_url = voiceURL.trim();
      }

      const created = await api.post<Call>("/api/v1/calls", body);
      setCallID(created.id);
      setCall(created);
    } catch (err) {
      setError(err instanceof ApiFailure ? describeFailure(err) : "error inesperado");
    } finally {
      setCalling(false);
    }
  }

  const ready =
    to.trim() !== "" && from.trim() !== "" &&
    (mode === "app" ? appID !== "" : voiceURL.trim() !== "");

  return (
    <div>
      <PageHeader
        title="Playground"
        description="Origina una llamada de prueba y mírala pasar en tiempo real."
      />

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="p-4">
          <h2 className="text-sm font-medium text-slate-100">Llamada</h2>
          <form className="mt-3 space-y-3" onSubmit={(e) => void place(e)}>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="A (destino)" hint="en E.164, con +">
                <input
                  className={inputClass}
                  placeholder="+528112345678"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                />
              </Field>
              <Field
                label="De (caller ID)"
                hint={
                  origenes !== null && origenes.length === 0
                    ? "No hay ninguno disponible: verifica un caller ID o pide un número a la plataforma."
                    : "Solo números de la cuenta y caller IDs verificados."
                }
              >
                {origenes === null ? (
                  <input className={inputClass} value="" disabled placeholder="Cargando…" />
                ) : origenes.length === 0 ? (
                  <Link
                    to="/caller-ids"
                    className="inline-block text-sm text-sky-300 underline decoration-sky-400/40 transition hover:text-sky-200"
                  >
                    Registrar un caller ID
                  </Link>
                ) : (
                  <select
                    className={inputClass}
                    value={from}
                    onChange={(e) => setFrom(e.target.value)}
                  >
                    <option value="">elegir…</option>
                    {origenes.map((numero) => (
                      <option key={numero} value={numero}>
                        {numero}
                      </option>
                    ))}
                  </select>
                )}
              </Field>
            </div>

            <Field
              label="Instrucciones"
              hint="De dónde saca la plataforma el XML al contestar. Una aplicación de voz es una Voice URL ya guardada, no un guion almacenado aquí."
            >
              <div className="flex gap-2 text-sm">
                <label className="flex items-center gap-1">
                  <input
                    type="radio"
                    checked={mode === "url"}
                    onChange={() => setMode("url")}
                  />
                  Voice URL
                </label>
                <label className="flex items-center gap-1">
                  <input
                    type="radio"
                    checked={mode === "app"}
                    onChange={() => setMode("app")}
                    disabled={apps.length === 0}
                  />
                  Aplicación de voz
                </label>
              </div>
            </Field>

            {mode === "url" ? (
              <Field label="Voice URL" hint="tiene que ser https y pública (guarda SSRF)">
                <input
                  className={inputClass}
                  placeholder="https://cliente.example.com/voz"
                  value={voiceURL}
                  onChange={(e) => setVoiceURL(e.target.value)}
                />
              </Field>
            ) : (
              <Field label="Aplicación">
                <select
                  className={inputClass}
                  value={appID}
                  onChange={(e) => setAppID(e.target.value)}
                >
                  <option value="">Elige una…</option>
                  {apps.map((app) => (
                    <option key={app.id} value={app.id}>
                      {app.name}
                    </option>
                  ))}
                </select>
              </Field>
            )}

            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Propósito" hint="se registra para auditoría">
                <select
                  className={inputClass}
                  value={purpose}
                  onChange={(e) => setPurpose(e.target.value)}
                >
                  <option value="other">Otro</option>
                  <option value="transactional">Transaccional</option>
                  <option value="marketing">Marketing</option>
                </select>
              </Field>
              <Field
                label="Detección de contestadora"
                hint="retrasa el primer verbo unos segundos"
              >
                <select
                  className={inputClass}
                  value={detection}
                  onChange={(e) => setDetection(e.target.value)}
                >
                  <option value="">La de la aplicación de voz</option>
                  <option value="off">No analizar</option>
                  <option value="enable">Al contestar</option>
                  <option value="detect_message_end">Al terminar el saludo del buzón</option>
                </select>
              </Field>
            </div>

            <Button type="submit" disabled={calling || !ready}>
              {calling ? "Encolando…" : "Llamar"}
            </Button>
          </form>

          {error && (
            <div className="mt-3 rounded-md border border-rose-500/30 bg-rose-500/10 p-3 text-xs text-rose-200">
              {error}
            </div>
          )}
        </Card>

        <Card className="p-4">
          <h2 className="text-sm font-medium text-slate-100">Seguimiento</h2>
          {call === null ? (
            <p className="mt-3 text-sm text-slate-500">
              Encola una llamada y aquí aparecerán sus estados a medida que pasan.
            </p>
          ) : (
            <>
              <div className="mt-3 flex items-center gap-3">
                <StatusBadge status={call.status} />
                {isLive(call.status) && (
                  <span className="text-xs text-slate-500">actualizando cada 2 s…</span>
                )}
                <Link
                  to={`/calls/${call.id}`}
                  className="ml-auto text-xs text-sky-300 underline decoration-sky-400/40 transition hover:text-sky-200"
                >
                  Ver detalle
                </Link>
              </div>

              <dl className="mt-3 grid gap-x-4 gap-y-1 text-sm sm:grid-cols-2">
                <div>
                  <dt className="text-xs text-slate-500">Identificador</dt>
                  <dd className="font-mono text-xs text-slate-100">{call.id}</dd>
                </div>
                <div>
                  <dt className="text-xs text-slate-500">Contestados</dt>
                  <dd className="text-slate-100">{call.answered_seconds} s</dd>
                </div>
                {call.end_reason && (
                  <div className="sm:col-span-2">
                    <dt className="text-xs text-slate-500">Motivo de fin</dt>
                    <dd>
                      <code className="text-xs">{call.end_reason}</code>
                    </dd>
                  </div>
                )}
              </dl>

              <ol className="mt-4 space-y-1">
                {events.map((event) => (
                  <li key={event.id} className="flex items-baseline gap-2 text-xs">
                    <span className="text-slate-600">{formatDateTime(event.occurred_at)}</span>
                    <Badge tone="neutral">{event.type}</Badge>
                  </li>
                ))}
                {events.length === 0 && (
                  <li className="text-xs text-slate-500">Sin eventos todavía.</li>
                )}
              </ol>
            </>
          )}
        </Card>
      </div>

      <section className="mt-6">
        <h2 className="mb-2 text-sm font-medium text-slate-100">Escribir markup</h2>
        <p className="mb-3 text-xs text-slate-500">
          El editor valida contra el mismo parser que ejecuta las llamadas. Para usarlo en
          una llamada tiene que servirlo tu Voice URL: el intérprete{" "}
          <strong>consulta</strong> una URL, no recibe el documento, y por eso aquí no hay
          un botón de «probar este XML» — sería simular una llamada que no se puede hacer.
        </p>
        <Card className="p-4">
          <MarkupEditor value={markup} onChange={setMarkup} rows={10} />
          <div className="mt-3 flex flex-wrap gap-2">
            {MARKUP_EXAMPLES.map((example) => (
              <button
                key={example.name}
                className="rounded-lg border border-white/15 bg-white/[0.04] px-2.5 py-1 text-xs text-slate-300 transition hover:bg-white/[0.09] hover:text-white"
                onClick={() => setMarkup(example.markup)}
              >
                {example.name}
              </button>
            ))}
          </div>
        </Card>
      </section>
    </div>
  );
}

/**
 * describeFailure traduce los códigos de rechazo a algo accionable.
 *
 * El mensaje del backend es correcto pero escueto; aquí se le añade qué hacer.
 * Los códigos son contrato (§6), así que esto no se rompe cuando cambie la
 * redacción del mensaje.
 */
function describeFailure(err: ApiFailure): string {
  const extra: Record<string, string> = {
    INSUFFICIENT_BALANCE: "Acredita saldo en la pantalla de Saldo.",
    DESTINATION_NOT_RATED:
      "El destino no está en el rate deck de la cuenta. Lo arregla la plataforma importando el prefijo.",
    DESTINATION_NOT_ALLOWED:
      "Revisa los prefijos y si el internacional está habilitado, en Límites.",
    COMPLIANCE_BLOCKED:
      "Puede ser el horario permitido o la lista de no-llamar. Revísalo en Límites.",
    SPEND_LIMIT_REACHED: "El límite de gasto diario se reinicia mañana en tu zona horaria.",
    TENANT_SUSPENDED: "La cuenta está suspendida: solo la plataforma puede reactivarla.",
    MFA_ENROLLMENT_REQUIRED: "Tu rol exige segundo factor. Configúralo para continuar.",
  };
  const hint = extra[err.error.code];
  return hint ? `${err.error.message}. ${hint}` : err.error.message;
}
